import { useCallback, useEffect, useReducer, useRef } from 'react';
import { api } from '@/lib/api';
import type { MissionEventPayload } from '@/hooks/useWebSocket';
import {
  NO_COVERAGE_READS,
  tallyCoverageReads,
  type CoverageReadTally,
  type MissionListItem,
} from '../missions/_components/missionListShared';
import { applyMissionEvent, reconcile } from '../missions/_components/missionQueueShared';

/**
 * The COP's operational mission queue: everything an operator can act on from
 * the live picture. Drafts are included so the panel can preview and hand off
 * to the planner — the panel itself decides how quietly to show them.
 * Completed/cancelled work belongs to History, never to the COP.
 */
export const COP_QUEUE_STATUSES = ['draft', 'scheduled', 'in_progress'] as const;

const QUEUE_QUERY = `/api/missions?status=${COP_QUEUE_STATUSES.join(',')}`;
/** Backstop cadence, matching the COP's other context polls (useCopContextData). */
const QUEUE_REFRESH_MS = 30_000;
/** Comfortably under the refresh interval, so a hung request cannot still be
 *  outstanding when the next one is issued. */
const QUEUE_REQUEST_TIMEOUT_MS = 10_000;

interface QueueState {
  missions: MissionListItem[];
  /** First load not yet answered — "no missions" must not render as truth yet. */
  loading: boolean;
  /** Last load failed — the panel is on stale data, never "all quiet". */
  loadError: boolean;
  /**
   * A poll has succeeded at least once in this session's lifetime (#471).
   *
   * NOT DERIVABLE FROM `missions.length`, which is what the panel used to infer it from. After a
   * FIRST-load failure the list is `[]`, `loadFailed` deliberately keeps it that way, and every
   * subsequent `mission_event` is therefore an INSERT rather than an update. One such frame makes
   * the list non-empty, and the panel then read that as "we have data, it is merely stale" — so
   * the degraded banner said "showing last known" over rows that were never known, beside a queue
   * still missing every mission the stream had not happened to mention.
   */
  everLoaded: boolean;
  /**
   * The last payload could not answer coverage for at least one mission (#650).
   *
   * NOT `loadError`, and not the panel's `coverageDegraded` either. The queue request succeeded —
   * a coverage lookup failure is a 200 with the fields omitted (#471) — and `coverageDegraded`
   * describes the SEPARATE `/api/coverage` poll, which stays perfectly healthy while this
   * endpoint's own coverage lookup is the thing that threw. Between them the panel had no way to
   * say the condition out loud, so it said nothing.
   *
   * A TALLY AND NOT A FLAG (`tallyCoverageReads`): the panel words the systemic case differently
   * from one bad row among many, and only the count can tell them apart.
   *
   * Set from the payload on a load, and recomputed from the merged list on a `mission_event` — the
   * only way the stream can raise the condition on rows it inserted itself, and the only thing
   * that retires the claim once a healthy stream has repaired a list a failing poll cannot.
   */
  coverageUnread: CoverageReadTally;
  /** Stream-applied ids by local apply time; see missionQueueShared.reconcile. */
  eventApplied: Map<string, number>;
}

type QueueAction =
  | { type: 'loaded'; startedAtMs: number; missions: MissionListItem[] }
  | { type: 'loadFailed' }
  | { type: 'missionEvent'; event: MissionEventPayload; receivedAtMs: number };

const initialQueue: QueueState = {
  missions: [],
  loading: true,
  loadError: false,
  everLoaded: false,
  coverageUnread: NO_COVERAGE_READS,
  eventApplied: new Map(),
};

function queueReducer(state: QueueState, action: QueueAction): QueueState {
  switch (action.type) {
    case 'loaded':
      return {
        ...state,
        ...reconcile(action.missions, state.missions, state.eventApplied, action.startedAtMs),
        loading: false,
        loadError: false,
        everLoaded: true,
        coverageUnread: tallyCoverageReads(action.missions),
      };
    case 'loadFailed':
      // Keeps the last good list — a failed refresh must not blank the queue, and for the same
      // reason `coverageUnread` is kept: it describes the rows still on screen.
      return { ...state, loading: false, loadError: true };
    case 'missionEvent': {
      const missions = applyMissionEvent(state.missions, action.event, COP_QUEUE_STATUSES);
      return {
        ...state,
        missions,
        coverageUnread: tallyCoverageReads(missions),
        eventApplied: new Map(state.eventApplied).set(action.event.mission_id, action.receivedAtMs),
      };
    }
    default:
      return state;
  }
}

/**
 * Live mission queue for the COP missions panel: `mission_event` frames are the
 * primary path, the poll is the backstop for a dropped socket — the same
 * two-writer model the dispatch board runs, on the same shared reconciliation.
 */
export function useCopMissionsQueue(
  subscribeMissionEvent: (cb: (event: MissionEventPayload) => void) => () => void,
) {
  const [state, dispatch] = useReducer(queueReducer, initialQueue);
  /** Issue order, so a slow response cannot overwrite a newer one. */
  const seqRef = useRef(0);

  const refresh = useCallback(() => {
    const seq = ++seqRef.current;
    const startedAtMs = Date.now();
    api
      .get<{ data: MissionListItem[] }>(QUEUE_QUERY, {
        signal: AbortSignal.timeout(QUEUE_REQUEST_TIMEOUT_MS),
      })
      .then((r) => {
        if (seq !== seqRef.current) return;
        dispatch({ type: 'loaded', startedAtMs, missions: r.data });
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        // The error rides along so an auth failure and a timeout are
        // distinguishable in the console.
        console.warn('Failed to load mission queue for COP', err);
        dispatch({ type: 'loadFailed' });
      });
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, QUEUE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(
    () =>
      subscribeMissionEvent((event) => {
        dispatch({ type: 'missionEvent', event, receivedAtMs: Date.now() });
      }),
    [subscribeMissionEvent],
  );

  return {
    missions: state.missions,
    loading: state.loading,
    loadError: state.loadError,
    everLoaded: state.everLoaded,
    coverageUnread: state.coverageUnread,
    refresh,
  };
}
